import { Divider, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/** STYLE */
import useStyles from 'features/setting/personnel-managements/personnels/style';

/** UTILS || SLICE */
import { editInfo, selectLoadingInfo, selectUserInfo } from 'app/slice/user.slice';

/** CONSTANT */
import { colors } from 'constants/theme';

/** COMPONENT */
import Box from '@mui/material/Box';
import ComponentApi from 'api/components-api';
import { ButtonInfoSimple } from 'component/Button/ButtonInfo';
import InputSecondNewDatePicker from 'component/Input/InputSecondNewDatePicker';
import AutocompleteSelect from 'component/Select/AutocompleteSelect';
import SelectInput from 'new-components/inputs/select-input';
import TextAreaInput from 'new-components/inputs/text-area-input';
import TextInput from 'new-components/inputs/text-input';

const initStateErrorMessage = {
  BRANCH_ADDRESS: ''
}
interface InformationProps {
  isEdit: boolean
  errorMessage?: any
  resetErrorMessage?: (type: string) => void
}
export default function Information(props: InformationProps) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const classes = useStyles()
  const loading = useSelector(selectLoadingInfo)
  const userInfo = useSelector(selectUserInfo)

  /** DATA OPTIONS */
  const [prefixOptions, setPrefixOptions] = useState<any[]>([])
  const [countryOptions, setCountryOptions] = useState<any[]>([])
  const [prefix, setPrefix] = useState<string>('')
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [firstnameEn, setFirstnameEn] = useState('')
  const [lastnameEn, setLastnameEn] = useState('')
  const [nickname, setNickname] = useState('')
  const [idcard, setIdcard] = useState('')
  const [birthday, setBirthday] = useState('')
  const [age, setAge] = useState<string>('')
  const [phone, setPhone] = useState('')
  const [telephone, setTelephone] = useState('')
  const [email, setEmail] = useState('')
  const [line, setLine] = useState('')
  const [facebook, setFacebook] = useState('')

  const [address, setAddress] = useState('')
  const [countryId, setCountryId] = useState(Number)
  const [provinceId, setProvinceId] = useState(Number)
  const [districtId, setDistrictId] = useState(Number)
  const [subDistrictId, setSubDistrictId] = useState(Number)
  const [postcode, setPostcode] = useState<any>('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  const clearErrorMessage = () => setErrorMessage(initStateErrorMessage)

  const calculateAge = (valueBirthday: any) => {
    const today = new Date()
    const birthDate = new Date(valueBirthday)
    let newAge = today.getFullYear() - birthDate.getFullYear()
    if (!valueBirthday) newAge = 0
    setAge(String(newAge))
  }

  const loadData = () => {
    ComponentApi.findAllPrefixes().then(({ data }) => data && setPrefixOptions(data))
    ComponentApi.findAllCountries().then(({ data }) => data && setCountryOptions(data))
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!loading) {
      setPrefix(userInfo.prefix || '')
      setFirstname(userInfo.firstname || '')
      setLastname(userInfo.lastname || '')
      setFirstnameEn(userInfo.firstnameEn || '')
      setLastnameEn(userInfo.lastnameEn || '')
      setNickname(userInfo.displayName || '')
      setIdcard(userInfo.idCard || '')
      setBirthday(userInfo.dateOfBirth || '')
      setTelephone(userInfo.telephone || '')
      setPhone(userInfo.phone || '')
      setEmail(userInfo.email || '')
      setFacebook(userInfo.facebook || '')
      setLine(userInfo.line || '')
      setCountryId(userInfo.countryId || '')
      setAddress(userInfo.address || '')
      setProvinceId(userInfo.provinceId || 0)
      setDistrictId(userInfo.districtId || 0)
      setSubDistrictId(userInfo.subDistrictId || 0)
      setPostcode(userInfo.postcode || 0)
      if (userInfo.dateOfBirth) calculateAge(userInfo.dateOfBirth)
    }
  }, [loading])


  const handleAddressChange = useCallback((addr: { provinceId?: number; districtId?: number; subDistrictId?: number; postcode?: string | number } | null) => {
    if (!addr) return
    if (addr.provinceId !== undefined) {
      setProvinceId(addr.provinceId)
      dispatch(editInfo({ key: 'provinceId', value: addr.provinceId }))
    }
    if (addr.districtId !== undefined) {
      setDistrictId(addr.districtId)
      dispatch(editInfo({ key: 'districtId', value: addr.districtId }))
    }
    if (addr.subDistrictId !== undefined) {
      setSubDistrictId(addr.subDistrictId)
      dispatch(editInfo({ key: 'subDistrictId', value: addr.subDistrictId }))
    }
    if (addr.postcode !== undefined) {
      setPostcode(addr.postcode)
      dispatch(editInfo({ key: 'postcode', value: addr.postcode.toString() }))
    }
  }, [dispatch])

  return (
    <Box sx={{ p: 3, width: '100%', height: 'calc(100vh - 35vh)', overflowY: 'auto', overflowX: 'hidden' }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{t('PERSONNEL.GENARAL')} </Typography>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={4} md={3}>
          <SelectInput
            required
            label={t('PERSONNEL.INPUT.INFORMATION.PREFIX')}
            value={prefix}
            onChange={e => {
              const v = e.target.value
              setPrefix(v)
              dispatch(editInfo({ key: 'prefix', value: v }))
              props.resetErrorMessage?.('info')
            }}
            options={prefixOptions.map((opt) => ({
              value: opt.prefixName,
              name: opt.prefixName
            }))}
            helperText={props.errorMessage.PREFIX}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            required
            label={t('PERSONNEL.INPUT.INFORMATION.FIRSTNAME')}
            value={firstname}
            onChange={e => {
              setFirstname(e.target.value)
              dispatch(editInfo({ key: 'firstname', value: e.target.value }))
              props.resetErrorMessage?.('info')
            }}
            helperText={props.errorMessage.FIRST_NAME}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            required
            label={t('PERSONNEL.INPUT.INFORMATION.LASTNAME')}
            value={lastname}
            onChange={e => {
              setLastname(e.target.value)
              dispatch(editInfo({ key: 'lastname', value: e.target.value }))
              props.resetErrorMessage?.('info')
            }}
            helperText={props.errorMessage.LAST_NAME}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            required
            label={t('PERSONNEL.INPUT.INFORMATION.FIRSTNAME_EN')}
            value={firstnameEn}
            onChange={e => {
              setFirstnameEn(e.target.value)
              dispatch(editInfo({ key: 'firstnameEn', value: e.target.value }))
              props.resetErrorMessage?.('info')
            }}
            helperText={props.errorMessage.FIRST_NAME_EN}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            required
            label={t('PERSONNEL.INPUT.INFORMATION.LASTNAME_EN')}
            value={lastnameEn}
            onChange={e => {
              setLastnameEn(e.target.value)
              dispatch(editInfo({ key: 'lastnameEn', value: e.target.value }))
              props.resetErrorMessage?.('info')
            }}
            helperText={props.errorMessage.LAST_NAME_EN}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            label={t('PERSONNEL.INPUT.INFORMATION.NICKNAME')}
            value={nickname}
            onChange={e => {
              setNickname(e.target.value)
              dispatch(editInfo({ key: 'displayName', value: e.target.value }))
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            label={t('PERSONNEL.INPUT.INFORMATION.ID_CARDNUMBER')}
            value={idcard}
            onChange={e => {
              setIdcard(e.target.value)
              dispatch(editInfo({ key: 'idCard', value: e.target.value }))
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <InputSecondNewDatePicker
                key={'date-of-birth'}
                label={t('PERSONNEL.INPUT.INFORMATION.BIRTHDAY')}
                value={birthday}
                onchange={(e: Date | null) => {
                  const date = moment(e).format('YYYY-MM-DD')
                  setBirthday(date)
                  dispatch(editInfo({ value: date, key: 'dateOfBirth' }))
                  calculateAge(date)
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextInput
                label={t('PERSONNEL.INPUT.INFORMATION.AGE')}
                value={age}
                disabled
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            label={t('PERSONNEL.INPUT.INFORMATION.PHONE')}
            value={phone}
            onChange={e => {
              setPhone(e.target.value)
              dispatch(editInfo({ key: 'phone', value: e.target.value }))
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            label={t('PERSONNEL.INPUT.INFORMATION.TELEPHONE')}
            value={telephone}
            onChange={e => {
              setTelephone(e.target.value)
              dispatch(editInfo({ key: 'telephone', value: e.target.value }))
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs>
              <TextInput
                label={t('PERSONNEL.INPUT.INFORMATION.EMAIL')}
                value={email}
                onChange={e => {
                  setEmail(e.target.value)
                  dispatch(editInfo({ key: 'email', value: e.target.value }))
                }}
                fullWidth
              />
            </Grid>
            <Grid item>
              <ButtonInfoSimple text={t('PERSONNEL.BUTTON.FORGOT_PASSWORD_SHARE')} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            label={t('PERSONNEL.INPUT.INFORMATION.LINE')}
            value={line}
            onChange={e => {
              setLine(e.target.value)
              dispatch(editInfo({ key: 'line', value: e.target.value }))
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextInput
            label={t('PERSONNEL.INPUT.INFORMATION.FACEBOOK')}
            value={facebook}
            onChange={e => {
              setFacebook(e.target.value)
              dispatch(editInfo({ key: 'facebook', value: e.target.value }))
            }}
            fullWidth
          />
        </Grid>
      </Grid>
      <Divider sx={{ mx: 0, mt: 3, mb: 0, bgcolor: colors.lightGray, opacity: 1, border: 'none' }} />
      <Typography sx={{ fontSize: 20, fontWeight: 600, mt: 2 }}>
        {t('PERSONNEL.INPUT.INFORMATION.ADDRESS')}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={4} md={3}>
          <AutocompleteSelect
            labelId="country"
            inputLabel={t('PERSONNEL.INPUT.INFORMATION.COUNTRY')}
            noOptionsText={t('REGISTER.FORM.NOT_FOUND')}
            options={countryOptions}
            getOptionLabel={opt => opt.countryNameTh}
            value={countryOptions.find(c => c.countryId === countryId) || null}
            onchange={(e, v) => {
              if (v) {
                setCountryId(v.countryId)
                dispatch(editInfo({ key: 'countryId', value: v.countryId }))
              }
            }}
            disableClearable
          />
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          {/* <SelectAddress
            patientId={0}
            countryId={countryId}
            provinceId={provinceId}
            districtId={districtId}
            subDistrictId={subDistrictId}
            postcode={postcode}
            callbackAddress={addr => {
              if (!addr) return;
              if (addr?.provinceId !== undefined) {
                setProvinceId(addr?.provinceId)
                dispatch(editInfo({ key: 'provinceId', value: addr?.provinceId }))
              }
              if (addr?.districtId !== undefined) {
                setDistrictId(addr?.districtId)
                dispatch(editInfo({ key: 'districtId', value: addr?.districtId }))
              }
              if (addr?.subDistrictId !== undefined) {
                setSubDistrictId(addr?.subDistrictId)
                dispatch(editInfo({ key: 'subDistrictId', value: addr?.subDistrictId }))
              }
              if (addr?.postcode !== undefined) {
                setPostcode(addr?.postcode)
                dispatch(editInfo({ key: 'postcode', value: `${addr?.postcode}` }))
              }
            }}
          /> */}
        </Grid>
        <Grid item xs={12}>
          <TextAreaInput
            label={t('PERSONNEL.INPUT.INFORMATION.ADDRESS_DETAIL')}
            value={address}
            helperText={props.errorMessage.BRANCH_ADDRESS}
            onChange={e => {
              setAddress(e.target.value)
              dispatch(editInfo({ key: 'address', value: e.target.value }))
              clearErrorMessage()
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    </Box>
  )
}
